<template>
  <Modal :name="$constants.dynamicModals.OPTIONAL_ANONYMITY_LEARN_MORE_MODAL" custom @before-close="onBeforeClose">
    <section
      class="anonymity-learn-more-modal"
      :class="{ 'anonymity-learn-more-modal--dark': $colorMode.preference === 'dark' }"
    >
      <BravadoButtonV3
        data-test="anonymity-learn-more-modal-close"
        class="anonymity-learn-more-modal__close"
        variant="blank"
        size="sm"
        :is-icon-button="true"
        @click="close"
      >
        <template #icon="{ iconSize }">
          <CloseIcon :width="iconSize" :height="iconSize" />
        </template>
        <span class="visually-hidden">Close</span>
      </BravadoButtonV3>
      <div class="anonymity-learn-more-modal__container">
        <BravadoText tag="h3" variant="header3">
          Control privacy with
          <span class="anonymity-learn-more-modal__highlight-text">Profiles</span>
        </BravadoText>
        <div class="anonymity-learn-more-modal__comparison">
          <AnonymityLearnMoreComparisonTable
            v-for="table in comparison"
            :key="table.title"
            class="anonymity-learn-more-modal__comparison-table"
            :title="table.title"
            :icon="table.icon"
            :description="table.description"
          />
        </div>
        <div class="anonymity-learn-more-modal__footer">
          <BravadoText tag="p" variant="headline-semibold">
            We never link your War Room Profile to your Public Profile and vice versa.
          </BravadoText>
          <div class="anonymity-learn-more-modal__actions">
            <BravadoButtonV3 variant="primary" @click="close">Makes sense</BravadoButtonV3>
          </div>
        </div>
      </div>
    </section>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, useContext } from '@nuxtjs/composition-api';
import AnonymityLearnMoreComparisonTable from '@/components/partials/optional-anonymity/AnonymityLearnMoreModal/AnonymityLearnMoreComparisonTable.vue';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import Modal from '@/components/common/Modal/Modal.vue';
import CloseIcon from '@/assets/svg/times.vue.svg';
import { OPTIONAL_ANONYMITY_LEARN_MORE_TOOLTIP } from '@/components/partials/optional-anonymity/constants';

type AnonymityLearnMoreModalEmits = {
  (event: 'on-submit'): void;
};

const emit = defineEmits<AnonymityLearnMoreModalEmits>();

const { $modal, $tooltip, $constants } = useContext();

const comparison = computed(() => [
  {
    title: 'Choose to be anonymous',
    icon: require('./assets/anonymous.svg'),
    description:
      'If you’d like to post anonymously.\nContent you create <b>will not be linked</b> to your real name and it will go to your anonymous profile.',
  },
  {
    title: 'Choose to post as you',
    icon: require('./assets/real-identity.png'),
    description:
      'If you’d like to build your brand.\nContent you create goes to your public portfolio and <b>linked to your real name.</b>',
  },
]);

function onBeforeClose() {
  if (!$tooltip.isPresent(OPTIONAL_ANONYMITY_LEARN_MORE_TOOLTIP)) {
    $tooltip.close(OPTIONAL_ANONYMITY_LEARN_MORE_TOOLTIP);
  }

  $modal.hide($constants.dynamicModals.OPTIONAL_ANONYMITY_LEARN_MORE_MODAL);
}

function close() {
  onBeforeClose();

  emit('on-submit');
}
</script>

<style lang="scss" scoped>
.anonymity-learn-more-modal {
  .bravado-theme--light & {
    box-shadow: var(--theme-elevation-15);
  }

  .bravado-theme--dark & {
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.35), 0 4px 10px rgba(255, 255, 255, 0.1);
  }

  position: relative;
  max-width: 584px;
  margin: 16px;
  border-radius: 16px;
  overflow: hidden;

  @include mq('sm') {
    padding-top: 252px;
    background-image: url('./assets/choose-profile.png');
    background-size: 276px 171px;
    background-repeat: no-repeat;
    background-position: center 54px;
    background-color: var(--theme-color-cyan-light);

    &--dark {
      background-color: var(--gray-70);
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    grid-gap: 16px;
    margin-top: 16px;

    @include mq('sm') {
      max-width: 336px;
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    grid-gap: 24px;
    padding: 40px 16px 24px;
    background-color: var(--theme-color-card);
    color: var(--theme-color-text);
    text-align: center;

    @include mq('sm') {
      padding-top: 16px;
    }
  }

  &__highlight-text {
    color: var(--blue-60);
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    cursor: pointer;
  }

  &__comparison {
    display: flex;
    flex-direction: column;
    width: 100%;
    grid-gap: 16px;

    &-table {
      width: 100%;
    }

    @include mq('sm') {
      flex-direction: row;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-gap: 12px;
  }
}
</style>
